import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../config/api';
import './Auth.css';

const CHOICES = [
  {
    id: 'participate',
    labelFr: 'Je participe au match',
    labelIt: 'Partecipo alla partita',
    descFr: 'Je confirme ma présence au stade pour ce match.',
    descIt: 'Confermo la mia presenza allo stadio per questa partita.',
    color: '#27ae60',
  },
  {
    id: 'available',
    labelFr: 'Je ne participe pas et je mets à dispo mon abo',
    labelIt: 'Non partecipo e metto a disposizione il mio abbonamento',
    descFr: 'Je ne viendrai pas et je mets mon abonnement à disposition du club.',
    descIt: 'Non verrò e metto il mio abbonamento a disposizione del club.',
    color: '#2980b9',
  },
  {
    id: 'keep',
    labelFr: 'Je ne participe pas et je garde mon abo',
    labelIt: 'Non partecipo e tengo il mio abbonamento',
    descFr: 'Je ne viendrai pas mais je conserve mon abonnement.',
    descIt: 'Non verrò ma conservo il mio abbonamento.',
    color: '#c0392b',
  },
];

const TicketSubscriberResponse = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/ticket-subscriber-responses/respond/${token}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Lien invalide ou expiré');
      } finally {
        setLoading(false);
      }
    };
    if (token) load();
  }, [token]);

  const handleSubmit = async (choice) => {
    if (!window.confirm(data?.lang === 'it' ? 'Confermare questa risposta?' : 'Confirmer cette réponse ?')) {
      return;
    }
    try {
      setSubmitting(true);
      const res = await api.post(`/ticket-subscriber-responses/respond/${token}`, { choice });
      setSuccess(res.data);
      setData((prev) => ({
        ...prev,
        alreadyResponded: true,
        choice: res.data.choice,
        choiceLabel: res.data.choiceLabel,
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const lang = data?.lang || 'fr';
  const isIt = lang === 'it';

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <p style={{ textAlign: 'center' }}>{isIt ? 'Caricamento…' : 'Chargement…'}</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1>{isIt ? 'Errore' : 'Erreur'}</h1>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container" style={{ maxWidth: '640px' }}>
        <h1>{isIt ? 'Conferma partecipazione' : 'Confirmation de participation'}</h1>

        {data?.firstName && (
          <p>
            {isIt ? `Buongiorno ${data.firstName},` : `Bonjour ${data.firstName},`}
          </p>
        )}

        <div
          style={{
            background: '#f8f9fa',
            padding: '1rem 1.25rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            border: '1px solid #e0e0e0',
          }}
        >
          <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '1.1rem' }}>
            {data.matchTitle}
          </p>
          <p style={{ margin: '0 0 4px', color: '#555' }}>{data.matchDateFormatted}</p>
          <p style={{ margin: 0, color: '#555' }}>{data.stadium}</p>
        </div>

        <p style={{ marginBottom: '1.5rem', color: '#555' }}>
          {isIt ? (
            <>
              Rispondi entro il <strong>{data.deadlineFormatted}</strong>.
            </>
          ) : (
            <>
              Merci de répondre avant le <strong>{data.deadlineFormatted}</strong>.
            </>
          )}
        </p>

        {data.expired && !data.alreadyResponded && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            {isIt
              ? 'Il termine per rispondere è scaduto.'
              : 'Le délai de réponse est dépassé.'}
          </div>
        )}

        {(success || data.alreadyResponded) && (
          <div
            className="success-message"
            style={{
              background: '#d4edda',
              color: '#155724',
              padding: '1rem',
              borderRadius: '6px',
              marginBottom: '1.5rem',
            }}
          >
            {isIt ? 'La tua risposta è stata registrata :' : 'Votre réponse a été enregistrée :'}
            <br />
            <strong>{success?.choiceLabel || data.choiceLabel}</strong>
          </div>
        )}

        {!data.alreadyResponded && !data.expired && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {CHOICES.map((c) => (
              <button
                key={c.id}
                type="button"
                disabled={submitting}
                onClick={() => handleSubmit(c.id)}
                style={{
                  textAlign: 'left',
                  padding: '1rem 1.25rem',
                  border: `2px solid ${c.color}`,
                  borderRadius: '8px',
                  background: '#fff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                <div style={{ fontWeight: 700, color: c.color, marginBottom: '4px' }}>
                  {isIt ? c.labelIt : c.labelFr}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                  {isIt ? c.descIt : c.descFr}
                </div>
              </button>
            ))}
          </div>
        )}

        {error && data && (
          <div className="error-message" style={{ marginTop: '1rem' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketSubscriberResponse;
